// ==================== PDP.jsx ====================
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productApi } from '../../services/productApi';
import { toPersianNumbers } from "../../utils/toPersianNumbers";
import ImageGallery from "../../components/ProductManagement/ImageGallery";
import Button from "../../components/Button";
import CategoryBreadCrumb from "../../components/Shop/CategoryBreadCrumb";
import api from "../../api"
import ProductCarousel from "../../components/Shop/ProductCarousel";
import AddToCartButton from "../../components/Basket/AddToCartButton";
import ProductReviewsSection from "../../components/Shop/ProductReviewsSection";
import { FaArrowRight, FaClock } from "react-icons/fa";

const PDP = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similarProductLoading, setSimilarProductLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);

  // داده‌های ثابت
  const staticData = {
    rating: 4.2,
    supplier: "سنگبری آذرخش",
    reviewsCount: 128,
    warranty: "18 ماه گارانتی",
    brand: "برند نمونه",
    sku: "PRD-2024-001",
    deliveryTime: "ارسال 24 ساعته",
  };

  const UNIT_TRANSLATIONS = {
    'count': 'عدد',
    'package': 'بسته',
    'weight': 'کیلو گرم',
    'length': 'متر',
    'area': 'متر مربع',
    'litr': 'لیتر',
    'volume': 'متر مکعب'
  };

  const viewRegisteredRef = useRef(false);

  useEffect(() => {
    if (!slug) return;
    fetchProductDetails();
    fetchSimilarProducts();
    const registerView = async () => {
      if (!viewRegisteredRef.current) {
        try {
          await api.post(`/products/shopping/${slug}/register_view/`)
          viewRegisteredRef.current = true;
        } catch (error) {
          console.error("register view failed", error);
        }
      }
    };

    registerView();
  }, [slug]);

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      const response = await productApi.getBySlug(slug);
      const product = response.data;
      setProductData(product);
    } catch (err) {
      setError('خطا در دریافت اطلاعات محصول');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSimilarProducts = async () => {
    setSimilarProductLoading(true)
    try {
      const response = await api.get(`/products/shopping/${slug}/similar-products/`)
      setProducts(response.data)
    } catch (err) {
      setError('خطا در دریافت اطلاعات محصول');
      console.error(err);
    } finally {
      setSimilarProductLoading(false);
    }
  }

  useEffect(() => {
    console.log('productData', productData)
  }, [slug]);

  // رندر امن مقدارها
  const renderValue = (value) => {
    if (value === null || value === undefined) return "ثبت نشده";
    if (typeof value === "object") {
      if (value.value !== undefined) return value.value;
      if (value.name !== undefined) return value.name;
      if (value.title !== undefined) return value.title;
      return JSON.stringify(value);
    }
    return value;
  };

  // ساخت لیست یکپارچه ویژگی‌ها از attribute_values + custom_attributes
  const buildAttributesList = () => {
    const list = [];

    if (productData.attribute_values && Array.isArray(productData.attribute_values)) {
      productData.attribute_values.forEach((attr) => {
        list.push({
          label: attr.attribute_name || attr.attribute?.name,
          value: renderValue(attr.value),
          unit: attr.attribute?.unit || attr.attribute_unit,
          is_featured: attr.is_featured || false,
        });
      });
    }

    if (Array.isArray(productData.custom_attributes)) {
      productData.custom_attributes.forEach((attr, idx) => {
        list.push({
          label: attr.title || `ویژگی ${idx + 1}`,
          value: `${renderValue(attr.value)}${attr.unit ? " " + attr.unit : ""}`,
          is_featured: attr.is_featured || false,
        });
      });
    }

    return list;
  };

  // آدرس تصویر
  const getImageUrl = (img) => {
    if (!img) return "";
    if (typeof img === 'object') {
      if (img.url) return img.url;
      if (img.image) return img.image;
    }
    if (typeof img === "string") return img;
    if (typeof File !== "undefined" && img instanceof File) {
      return URL.createObjectURL(img);
    }
    if (typeof Blob !== "undefined" && img instanceof Blob) {
      return URL.createObjectURL(img);
    }
    return "";
  };

  // تبدیل images از API به فرمت مورد نیاز ImageGallery
  const formatImagesForGallery = () => {
    if (!productData?.images) return [];
    return productData.images.map(img => ({
      id: img.id,
      order: img.order,
      url: img.image_url || img.image || '',
      thumbnail_url: img.thumbnail_url || img.image_url || img.image || '',
      is_featured: img.is_featured || false,
    }));
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-b-[#1E3A5F]"></div>
        <p className="mt-4 text-sm text-gray-600">در حال دریافت اطلاعات محصول...</p>
      </div>
    );
  }

  if (error || !productData) {
    return (
      <div className="p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-[#E74C3C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">{error || 'محصول یافت نشد'}</h3>
        <Button variant="primary" className="mt-4 mx-auto" onClick={() => navigate(-1)}>
          بازگشت به لیست
        </Button>
      </div>
    );
  }

  const allAttributes = buildAttributesList();
  const topAttributes = allAttributes.filter(attr => attr.is_featured);
  const hasAnyAttribute = allAttributes.length > 0;

  const isProductComplete = productData.name && productData.category && productData.min_price;
  const categoryName = productData.category_full_path;
  const imagesForGallery = formatImagesForGallery();

  const discount = productData.max_discount || 0;
  const finalPrice = productData.price * (1 - discount / 100);
  const canBuy = productData.min_price && productData.has_stock;

  return (
    <div className="px-3 py-4 sm:p-4 md:p-6">
      {/* ============ نوار بالا: بازگشت + مسیر دسته‌بندی ============ */}
      <div className="mb-3 md:mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="shrink-0 flex items-center justify-center h-8 w-8 md:h-auto md:w-auto rounded-full md:rounded-none bg-gray-100 md:bg-transparent text-gray-600 hover:text-gray-900 md:gap-1 md:px-0"
          aria-label="بازگشت"
        >
          <FaArrowRight className="text-sm md:hidden" />
          <span className="hidden md:flex items-center gap-1 text-sm">
            <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            بازگشت
          </span>
        </button>
        <div className="min-w-0 flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden">
          <CategoryBreadCrumb
            categoryFullPath={productData.category_full_path}
            categorySlugFullPath={productData.category_slug_full_path}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
        <main className="w-full lg:w-3/4">
          {/* ستون تصویر و اطلاعات */}
          <div className="grid lg:grid-cols-2 gap-4 md:gap-6 items-start mb-6">
            {/* گالری تصاویر */}
            <div>
              <ImageGallery
                images={imagesForGallery}
                productName={productData.name}
              />
            </div>

            {/* عنوان و اطلاعات محصول */}
            <div className="mb-2 md:mb-6 text-right">
              <h1 className="text-lg md:text-[20px] leading-snug text-[#1E3A5F] font-semibold">
                {productData.name || "نام محصول ثبت نشده"}
              </h1>
              <div className="mt-1.5 text-xs md:text-sm text-gray-600">
                {productData.brand || staticData.brand} / {categoryName}
              </div>
              <div className="mt-1 flex items-center gap-1 text-[11px] md:text-xs text-gray-500">
                <span>{staticData.supplier}</span>
                <span className="text-gray-300">•</span>
                <span>امتیاز {toPersianNumbers(staticData.rating)} از {toPersianNumbers(staticData.reviewsCount)} نظر</span>
              </div>

              {topAttributes.length > 0 ? (
                <>
                  <h2 className="mt-4 md:mt-[24px] text-sm md:text-base font-semibold text-gray-900 mb-2 md:mb-3">ویژگی‌ها</h2>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {topAttributes.map((attr, idx) => (
                      <li key={idx}>
                        {attr.label} : <span className="font-medium text-gray-900">{toPersianNumbers(attr.value)}</span>
                        {attr.unit && <span className="font-medium text-gray-900"> {toPersianNumbers(attr.unit)}</span>}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="mt-4 text-sm text-gray-400">هیچ ویژگی‌ای ثبت نشده است.</p>
              )}
            </div>
          </div>

          {/* بخش توضیحات */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-sm md:text-base font-semibold text-gray-900 mb-3">توضیحات</h2>
            <div className="bg-gray-50 rounded-2xl border border-gray-200 px-4 py-4 md:py-5 text-sm leading-relaxed text-gray-700">
              {productData.description ? (
                productData.description.split("\n").map((line, index) => (
                  <p key={index} className={line ? "mb-2" : "mb-4"}>
                    {line || <br />}
                  </p>
                ))
              ) : (
                <p className="text-gray-400">هیچ توضیحی ثبت نشده است.</p>
              )}
            </div>
          </section>

          {/* روش‌های ارسال و زمان تحویل - در موبایل داخل جریان اصلی */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6 md:hidden">
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4">
              <h3 className="mb-3 text-sm font-semibold text-[#1E3A5F]">روش‌های ارسال</h3>
              <ul className="space-y-1.5 text-sm text-gray-700">
                {productData.delivery_by_supplier && <li>ارسال توسط تأمین کننده</li>}
                {productData.delivery_by_customer && <li>تحویل توسط مشتری</li>}
                {productData.delivery_by_carrier && <li>ارسال توسط شرکت حمل</li>}
                {!productData.delivery_by_supplier &&
                  !productData.delivery_by_customer &&
                  !productData.delivery_by_carrier && (
                    <li className="text-gray-400">روش ارسال ثبت نشده است</li>
                  )}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">زمان تحویل</h3>
              <div className="flex items-center gap-3">
                <div className="bg-[#00AFA9]/10 p-3 rounded-xl">
                  <FaClock className="w-5 h-5 text-[#00AFA9]" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-900">{toPersianNumbers(staticData.deliveryTime)}</div>
                  <div className="text-[11px] text-gray-500">در شهرهای اصلی</div>
                </div>
              </div>
            </div>
          </div>

          {/* تمام ویژگی‌ها */}
          {hasAnyAttribute && (
            <section className="mt-4 mb-6 md:mb-4">
              <h2 className="text-sm md:text-base font-semibold text-gray-900 mb-3">ویژگی‌ها</h2>
              <div className="space-y-1 text-sm text-gray-700">
                {allAttributes.map((attr, idx) => (
                  <div key={idx}>
                    {attr.label} : <span className="font-medium text-gray-900">{toPersianNumbers(attr.value)}</span>
                    {attr.unit && <span className="font-medium text-gray-900"> {toPersianNumbers(attr.unit)}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="mb-8">
            <h2 className="text-sm md:text-base font-semibold text-gray-900 my-4 md:my-6">نظرات خریداران</h2>
            <ProductReviewsSection productId={productData.id} />
          </section>
        </main>

        {/* ستون سمت چپ - فقط دسکتاپ (در موبایل قیمت/افزودن در نوار پایین ثابت است) */}
        <aside className="hidden lg:block w-full lg:w-1/4 space-y-6">
          <div>
            <h3 className="text-base mb-2 text-[#1E3A5F] font-semibold">
              {productData.created_by_name || "نام تأمین کننده"}
            </h3>
            <div className="my-2 text-xs text-gray-500">
              امتیاز {toPersianNumbers(staticData.rating)} از {toPersianNumbers(staticData.reviewsCount)} نظر
            </div>
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4">
              <h3 className="mb-4 text-[18px] text-[#1E3A5F] font-semibold">قیمت</h3>
              {canBuy ? (
                <div>
                  <div className="flex items-center justify-between">
                    {discount > 0 && (
                      <span className="bg-[#E74C3C] text-white px-2 py-1 rounded-md text-xs font-bold">
                        {toPersianNumbers(discount)}٪ تخفیف
                      </span>
                    )}
                    <div className="flex flex-col">
                      <div className="text-lg font-bold text-gray-900">
                        {Number(finalPrice).toLocaleString('fa-IR')}
                        <span className="text-sm text-gray-500 mr-1">تومان</span>
                      </div>
                      {discount > 0 && (
                        <div className="text-xs text-gray-400 line-through">
                          {Number(productData.price).toLocaleString('fa-IR')}
                          <span className="mr-1 text-gray-400">تومان</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-3 mb-4">
                    واحد اندازه گیری: {UNIT_TRANSLATIONS[productData.unit_of_measure]}
                  </div>
                  <div className="flex items-center">
                    <AddToCartButton
                      product={productData}
                      onSuccess={(result) => {
                        console.log("Added to cart:", result);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <span className="text-gray-500">ناموجود</span>
              )}
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-4">
            <h3 className="mb-1 text-[18px] text-[#1E3A5F] font-semibold">روش‌های ارسال</h3>
            <ul className="space-y-1 text-sm text-gray-700">
              {productData.delivery_by_supplier && <li>ارسال توسط تأمین کننده</li>}
              {productData.delivery_by_customer && <li>تحویل توسط مشتری</li>}
              {productData.delivery_by_carrier && <li>ارسال توسط شرکت حمل</li>}
              {!productData.delivery_by_supplier &&
                !productData.delivery_by_customer &&
                !productData.delivery_by_carrier && (
                  <li className="text-gray-400">روش ارسال ثبت نشده است</li>
                )}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">زمان تحویل</h3>
            <div className="flex items-center gap-3">
              <div className="bg-[#00AFA9]/10 p-3 rounded-xl">
                <FaClock className="w-5 h-5 text-[#00AFA9]" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">{toPersianNumbers(staticData.deliveryTime)}</div>
                <div className="text-xs text-gray-600">در شهرهای اصلی</div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {products.length > 0 && (
        <div className="mt-6 mb-6">
          <div className="flex overflow-x-auto mb-1">
            <ProductCarousel
              title={'کالاهای مشابه'}
              products={products}
              seeAllLink={`/shopping/product-list/?category_slug=${productData.category_slug}`}
            />
          </div>
        </div>
      )}

      {/* ============ نوار پایین ثابت قیمت + افزودن به سبد (فقط موبایل) ============ */}
      {canBuy && (
        <div className="md:hidden fixed bottom-16 inset-x-0 z-[100] border-t border-gray-200 bg-white px-3 py-2.5 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              {discount > 0 && (
                <span className="inline-block bg-[#E74C3C] text-white text-[10px] font-bold rounded-md px-1.5 py-0.5 mb-1">
                  {toPersianNumbers(discount)}٪ تخفیف
                </span>
              )}
              <div className="flex items-baseline gap-1.5">
                <span className="text-base font-bold text-gray-900 truncate">
                  {Number(finalPrice).toLocaleString('fa-IR')}
                </span>
                <span className="text-[11px] text-gray-500 shrink-0">تومان</span>
              </div>
              {discount > 0 && (
                <div className="text-[11px] text-gray-400 line-through">
                  {Number(productData.price).toLocaleString('fa-IR')} تومان
                </div>
              )}
            </div>
            <div className="shrink-0">
              <AddToCartButton
                product={productData}
                onSuccess={(result) => {
                  console.log("Added to cart:", result);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* فاصله‌ساز برای جلوگیری از پوشیده‌شدن محتوا زیر نوار ثابت موبایل */}
      {canBuy && <div className="h-24 md:hidden" />}
    </div>
  );
};

export default PDP;